import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth'
import { useSyncStore } from '@/store/sync'

const WS_URL = import.meta.env.VITE_WS_URL as string | undefined

export function useGroupWebSocket(groupId: string | null): void {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const setSyncComplete = useSyncStore((s) => s.setSyncComplete)
  const wsRef = useRef<WebSocket | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryCountRef = useRef(0)
  const unmountedRef = useRef(false)

  useEffect(() => {
    unmountedRef.current = false
    if (!WS_URL || !groupId || !user?.token) return

    function connect(): void {
      if (unmountedRef.current) return
      const url = `${WS_URL}?token=${encodeURIComponent(user!.token)}&groupId=${groupId}`
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        retryCountRef.current = 0
      }

      ws.onmessage = (event: MessageEvent<string>) => {
        try {
          const msg = JSON.parse(event.data) as { type: string; queryKeys?: string[][] }
          if (msg.type === 'invalidate' && msg.queryKeys) {
            for (const key of msg.queryKeys) {
              void queryClient.invalidateQueries({ queryKey: key })
            }
          } else if (msg.type === 'sync_complete') {
            if (msg.queryKeys) {
              for (const key of msg.queryKeys) {
                void queryClient.invalidateQueries({ queryKey: key })
              }
            }
            setSyncComplete()
          }
        } catch { /* malformed — ignore */ }
      }

      ws.onclose = () => {
        if (unmountedRef.current) return
        const delay = Math.min(1000 * 2 ** retryCountRef.current, 30_000)
        retryCountRef.current++
        retryTimerRef.current = setTimeout(connect, delay)
      }

      ws.onerror = () => ws.close()
    }

    connect()

    return () => {
      unmountedRef.current = true
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.onclose = null // prevent retry on intentional close
        wsRef.current.close()
        wsRef.current = null
      }
      retryCountRef.current = 0
    }
  }, [groupId, user?.token])
}
